import { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ENUM_ORDER_PAYMENT_STATUS, useGetOrder, useMutateOrderCancel, useMutateUpdateOrder } from 'queries/orders.query';
import {
  Avatar,
  Badge,
  BlockStack,
  Box,
  DataTable,
  Divider,
  Grid,
  InlineGrid,
  InlineStack,
  LegacyCard,
  Link,
  Page,
  ResourceItem,
  ResourceList,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
} from '@shopify/polaris';
import { Helmet } from 'react-helmet-async';
import { XSmallIcon } from '@shopify/polaris-icons';
import dateandtime from 'date-and-time';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleDollarToSlot, faCommentSms, faTicket } from '@fortawesome/free-solid-svg-icons';
import EditOrderTaxModal from 'entities/ticket-managers/components/editOrderTax.modal';
import { getOrderStatus } from './order.util';
import __helpers from 'helpers/index';
import EditOrderModal from './edit_order.modal';

export default function OrderDetail() {
  let { order_id } = useParams();
  const navigate = useNavigate();

  const { data: entity, isLoading: loadingOrderDetail } = useGetOrder(order_id);
  const { mutateAsync: orderCancel, isPending: cancelling, isSuccess } = useMutateOrderCancel();

  const orderCancelCallback = useCallback(async () => {
    let result = window.confirm('Bạn có thực sự muốn hủy đơn này và các vé liên quan?');
    try {
      if (result) await orderCancel(order_id);
    } catch (e) {}
  }, [order_id]);

  /** Edit thông tin thuế */
  const currentOrderForEditingTax = useRef(null);
  const [showEditTaxModal, setShowEditTaxModal] = useState(false);
  const editTaxButtonCallbackFinal = useCallback(async (order_id: string) => {
    currentOrderForEditingTax.current = order_id;
    setShowEditTaxModal(true);
  }, []);
  // new one

  /** edit thông tin vé */
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);

  return (
    <>
      <Helmet>
        <title>Chi tiết đơn hàng</title>
      </Helmet>
      <Page
        backAction={{
          content: 'Quay lại danh sách',
          onAction: () => navigate('/orders'),
        }}
        title={`Đơn hàng ${entity?.order_code}`}
        subtitle={`Ngày đặt đơn ${dateandtime.format(new Date(Number(entity?.createdAt)), 'HH:mm DD/MM/YYYY')}`}
        compactTitle
        titleMetadata={
          <InlineStack gap={'200'}>
            {entity?.payment_status === ENUM_ORDER_PAYMENT_STATUS.PAID ? <Badge tone="success">PAID</Badge> : <Badge tone="attention">UNPAY</Badge>}
            {getOrderStatus(entity?.order_status)}
          </InlineStack>
        }
        primaryAction={{
          content: 'Hủy đơn',
          icon: XSmallIcon,
          disabled: cancelling,
          onAction: () => orderCancelCallback(),
        }}
      >
        {loadingOrderDetail ? <SkeletonBodyText /> : null}
        <Text as="h2">
          Ngày đi &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong>{dateandtime.format(new Date(Number(entity?.departure_date)), 'HH:mm DD/MM/YYYY')}</strong>
        </Text>
        <Text as="h2">
          Hành trình &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong>
            {entity?.journey?.journey_from} - {entity?.journey?.journey_to}
          </strong>
        </Text>
        {entity?.note ? (
          <Text as="p" tone="critical">
            Ghi chú đơn hàng: {entity?.note}
          </Text>
        ) : null}
        <Text as="p">
          Tổng thanh toán <strong>{__helpers.formatNumberWithCommas(entity?.total_must_pay)} VND</strong>
        </Text>

        <br />
        <LegacyCard>
          <LegacyCard.Header
            title="Thông tin hành khách"
            actions={[
              {
                content: 'Chỉnh sửa',
                onAction: () => setShowEditOrderModal(true),
              },
            ]}
          ></LegacyCard.Header>
          <LegacyCard.Section>
            {entity ? (
              <BlockStack gap="200">
                <div>
                  {entity?.customer_data?.display_name} ({entity?.customer_data?.user_phonenumber})
                </div>
                <Divider />

                <div>Đón tại: {entity?.pickup_address} </div>
                <div>Xuống tại: {entity?.dropoff_address}</div>

                {entity?.dropoff_contact_name ? (
                  <>
                    <Divider />
                    <div>
                      Thông tin trả: {entity?.dropoff_contact_name} - {entity?.dropoff_contact_phonenumber}
                    </div>
                  </>
                ) : null}
              </BlockStack>
            ) : (
              <Text as="p" tone="subdued">
                Chưa có thông tin
              </Text>
            )}
          </LegacyCard.Section>
        </LegacyCard>

        <LegacyCard title="Thông tin vé" sectioned>
          <DataTable
            hideScrollIndicator
            hasZebraStripingOnData
            columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric']}
            headings={['Số vé', 'Loại vé', 'Giá', 'K.mại', 'Ghế']}
            rows={
              entity?.ticket
                ? entity?.ticket.map((item) => {
                    const { ticket_id, ticket_type, price, discount, trip_manifest_ticket } = item;
                    const seat_position = trip_manifest_ticket?.seat_position;

                    let trip_hour = dateandtime.format(new Date(Number(entity?.departure_date)), 'HH:mm');
                    let trip_date_reformat = dateandtime.format(new Date(Number(entity?.departure_date)), 'YYYY-MM-DD');

                    const url = `/ticket_managers?d=${trip_date_reformat}&journey_id=${entity?.journey_id}&htext=${trip_hour}&h=${trip_manifest_ticket?.trip_manifest?.time_id}#${entity?.id}`;

                    return [
                      <Link url={url}>
                        <FontAwesomeIcon icon={faTicket} /> {ticket_id}
                      </Link>,
                      ticket_type,
                      <div>{__helpers.formatNumberWithCommas(price)}</div>,
                      <div>{__helpers.formatNumberWithCommas(discount)}</div>,
                      <div>{seat_position || '----'}</div>,
                    ];
                  })
                : []
            }
          />

          <Divider />
          <br />
          <Text as="h3" variant="headingMd" alignment="end" fontWeight="bold">
            Tổng vé: {__helpers.formatNumberWithCommas(entity?.total_seat)}
          </Text>
        </LegacyCard>

        <LegacyCard title="Phí đơn hàng" sectioned>
          <ResourceList
            resourceName={{ singular: 'phí', plural: 'phí' }}
            items={entity?.order_fee || []}
            renderItem={(item) => {
              return (
                <ResourceItem
                  verticalAlignment="center"
                  id={`order_fee_` + item.fee_value}
                  url={null}
                  media={<FontAwesomeIcon icon={faCircleDollarToSlot} />}
                  accessibilityLabel={``}
                  //   name={name}
                >
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="bodyMd" fontWeight="bold" as="h3">
                      {item.fee_description}
                    </Text>
                    <div>{__helpers.formatNumberWithCommas(item.fee_value)}</div>
                  </InlineStack>
                </ResourceItem>
              );
            }}
          />
          <Divider />
          <br />
          <Text as="h3" variant="headingMd" alignment="end" fontWeight="bold">
            Tổng phí: {__helpers.formatNumberWithCommas(entity?.total_fee)}
          </Text>
        </LegacyCard>
        <LegacyCard>
          <LegacyCard.Header
            title="Thông tin xuất thuế"
            actions={[
              {
                content: 'Chỉnh sửa',
                onAction: () => editTaxButtonCallbackFinal(entity.id),
              },
            ]}
          ></LegacyCard.Header>
          <LegacyCard.Section>
            {entity?.order_invoice_info ? (
              <BlockStack gap="400">
                <div>Loại thuế: {entity?.order_invoice_info?.tax_type}</div>
                <div>Mã số thuế: {entity?.order_invoice_info?.tax_code}</div>
                <div>Người mua: {entity?.order_invoice_info?.name}</div>
                <div>Tên người mua: {entity?.order_invoice_info?.buyer_name}</div>
                <div>Phone: {entity?.order_invoice_info?.invoice_phone}</div>
                <div>Email: {entity?.order_invoice_info?.invoice_email}</div>
                <div>Address: {entity?.order_invoice_info?.invoice_address}</div>
              </BlockStack>
            ) : (
              <Text as="p" tone="subdued">
                Xuất thuế theo thông tin khách lẻ mặc định
              </Text>
            )}
          </LegacyCard.Section>
        </LegacyCard>
      </Page>
      <br />
      <br />
      <EditOrderTaxModal open={showEditTaxModal} orderId={currentOrderForEditingTax.current} onClose={() => setShowEditTaxModal(false)} />
      <EditOrderModal show={showEditOrderModal} order_id={order_id} onClose={() => setShowEditOrderModal(false)} />
    </>
  );
}

